import type { BorrowTokenOption, EulerVaultInfo, SolverType } from '../constants'
import type { MergedPosition } from '../hooks/useAllEulerPositions'
import { getRelevantEulerPositions } from './cardConfiguration.utils'

export interface AddCardWizardOption {
  key: string
  title: string
  subtitle: string
  market: 'hypurrFi' | 'euler'
  mode: 'borrow' | 'spend'
  solverType: SolverType
  cluster?: 'prime' | 'yield'
}

interface GetAddCardWizardOptionsParams {
  hasHypurrFiBorrow: boolean
  hasHypurrFiSpend: boolean
  hasPrimeEulerBorrow: boolean
  hasYieldEulerBorrow: boolean
  hasPrimeEulerSpend: boolean
  hasYieldEulerSpend: boolean
}

interface GetWizardBorrowTokenOptionsParams {
  market: 'hypurrFi' | 'euler'
  mode: 'borrow' | 'spend'
  hypurrFiBorrowTokens: BorrowTokenOption[]
  hypurrFiWithdrawTokens: BorrowTokenOption[]
}

interface GetWizardEulerPositionsParams {
  allPositions: MergedPosition[]
  activeEulerVaultAddresses: string[]
  activeEulerCollateralVaultAddresses?: string[]
}

interface WizardTokenFieldMetaParams {
  market: 'hypurrFi' | 'euler'
  mode: 'borrow' | 'spend'
}

interface GetInitialWizardEulerVaultParams {
  market: 'hypurrFi' | 'euler'
  availableEulerVaults: EulerVaultInfo[]
  previousSelectedVaultAddress?: string
}

interface GetWizardPositionSelectValueParams {
  isNewPosition: boolean
  subAccountId: number
  visibleSubAccountIds: number[]
}

export function getAddCardWizardOptions({
  hasHypurrFiBorrow,
  hasHypurrFiSpend,
  hasPrimeEulerBorrow,
  hasYieldEulerBorrow,
  hasPrimeEulerSpend,
  hasYieldEulerSpend,
}: GetAddCardWizardOptionsParams): {
  borrowOptions: AddCardWizardOption[]
  spendOptions: AddCardWizardOption[]
} {
  const borrowOptions: AddCardWizardOption[] = []
  const spendOptions: AddCardWizardOption[] = []

  if (hasHypurrFiBorrow) {
    borrowOptions.push({
      key: 'hypurrFi-borrow',
      title: 'Pooled',
      subtitle: 'HypurrFi pooled lending',
      market: 'hypurrFi',
      mode: 'borrow',
      solverType: 'hypurrFi',
    })
  }

  if (hasPrimeEulerBorrow) {
    borrowOptions.push({
      key: 'euler-borrow-prime',
      title: 'Prime',
      subtitle: 'HypurrFi Prime market',
      market: 'euler',
      mode: 'borrow',
      solverType: 'euler',
      cluster: 'prime',
    })
  }

  if (hasYieldEulerBorrow) {
    borrowOptions.push({
      key: 'euler-borrow-yield',
      title: 'Yield',
      subtitle: 'HypurrFi Yield market',
      market: 'euler',
      mode: 'borrow',
      solverType: 'euler',
      cluster: 'yield',
    })
  }

  if (hasHypurrFiSpend) {
    spendOptions.push({
      key: 'hypurrFi-spend',
      title: 'Pooled',
      subtitle: 'Spend from HypurrFi deposits',
      market: 'hypurrFi',
      mode: 'spend',
      solverType: 'hypurrFi-withdraw',
    })
  }

  if (hasPrimeEulerSpend) {
    spendOptions.push({
      key: 'euler-spend-prime',
      title: 'Prime',
      subtitle: 'Spend from HypurrFi Prime deposits',
      market: 'euler',
      mode: 'spend',
      solverType: 'euler-withdraw',
      cluster: 'prime',
    })
  }

  if (hasYieldEulerSpend) {
    spendOptions.push({
      key: 'euler-spend-yield',
      title: 'Yield',
      subtitle: 'Spend from HypurrFi Yield deposits',
      market: 'euler',
      mode: 'spend',
      solverType: 'euler-withdraw',
      cluster: 'yield',
    })
  }

  return {
    borrowOptions,
    spendOptions,
  }
}

export function getWizardBorrowTokenOptions({
  market,
  mode,
  hypurrFiBorrowTokens,
  hypurrFiWithdrawTokens,
}: GetWizardBorrowTokenOptionsParams): BorrowTokenOption[] {
  if (market !== 'hypurrFi') {
    return []
  }

  return mode === 'borrow' ? hypurrFiBorrowTokens : hypurrFiWithdrawTokens
}

export function getWizardEulerPositions({
  allPositions,
  activeEulerVaultAddresses,
  activeEulerCollateralVaultAddresses,
}: GetWizardEulerPositionsParams): MergedPosition[] {
  return getRelevantEulerPositions({
    allEulerPositions: allPositions,
    activeEulerVaultAddresses,
    activeEulerCollateralVaultAddresses,
  })
}

export function getInitialWizardEulerVault({
  market,
  availableEulerVaults,
  previousSelectedVaultAddress,
}: GetInitialWizardEulerVaultParams): EulerVaultInfo | null {
  if (market !== 'euler') {
    return null
  }

  if (previousSelectedVaultAddress) {
    const previousLower = previousSelectedVaultAddress.toLowerCase()
    const matchingVault = availableEulerVaults.find((vault) => vault.address.toLowerCase() === previousLower)
    if (matchingVault) {
      return matchingVault
    }
  }

  return availableEulerVaults[0] ?? null
}

export function getWizardTokenFieldMeta({
  market,
  mode,
}: WizardTokenFieldMetaParams): {
  label: string
  toneClassName: string
  textClassName: string
  description: string
} {
  if (market === 'hypurrFi' && mode === 'borrow') {
    return {
      label: 'Borrow Token',
      toneClassName: 'border-blue-500/20 bg-blue-500/5',
      textClassName: 'text-blue-300',
      description:
        "Borrow against your HypurrFi collateral. After creating this card, you'll set up a delegation to authorize spending.",
    }
  }

  if (market === 'euler' && mode === 'spend') {
    return {
      label: 'Spend Asset',
      toneClassName: 'border-green-500/20 bg-green-500/5',
      textClassName: 'text-green-300',
      description: "Spend directly from your Euler deposits. Choose the collateral you'd like this card to use.",
    }
  }

  return {
    label: 'Withdrawal Token',
    toneClassName: 'border-green-500/20 bg-green-500/5',
    textClassName: 'text-green-300',
    description:
      "Spend directly from your HypurrFi deposits. After creating this card, you'll approve a spend allowance from your deposit balance.",
  }
}

export function getWizardSolverModeLabel(mode: 'borrow' | 'spend'): 'Borrow' | 'Spend' {
  return mode === 'borrow' ? 'Borrow' : 'Spend'
}

export function getWizardPositionSelectValue({
  isNewPosition,
  subAccountId,
  visibleSubAccountIds,
}: GetWizardPositionSelectValueParams): string {
  if (isNewPosition || !visibleSubAccountIds.includes(subAccountId)) {
    return 'new'
  }

  return String(subAccountId)
}
